import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import type { Campaign } from '../../types/campaign';

// Typed-confirmation modal mirroring DeleteAccountModal. Campaign delete is
// a hard delete -- admin-only on the server; the "archive" status is the
// normal close-out path. Built on the shadcn Dialog (Phase 2F) — Radix owns
// the overlay, focus trap, Esc and close button.

const CONFIRM_WORD = 'DELETE';

export type DeleteCampaignModalProps = {
  campaign: Campaign;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

export function DeleteCampaignModal({ campaign, onClose, onConfirm }: DeleteCampaignModalProps) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: onConfirm,
    onError: (e: Error) => setError(e.message),
  });

  const ready = typed === CONFIRM_WORD && !mutation.isPending;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete campaign permanently?</DialogTitle>
          <DialogDescription>
            This removes the Campaign document. It can't be undone. To keep the
            Campaign but hide it from the rollout picker, set status to{' '}
            <span className="font-mono">archived</span> instead.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-foreground">
          <span className="font-medium">{campaign.name}</span>{' '}
          <span className="text-muted-foreground font-mono">({campaign.slug})</span>
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="delete-campaign-confirm">
            Type <span className="font-mono">{CONFIRM_WORD}</span> to confirm:
          </Label>
          <Input
            id="delete-campaign-confirm"
            className="font-mono"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
          />
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!ready}
            variant="destructive"
          >
            {mutation.isPending ? 'Deleting...' : 'Delete permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
